import React, { FunctionComponent, Fragment, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ResumeSection, { BodyResumeSection } from '../ResumeSection';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Service, StaffMember } from 'model/Service';
import StaffChip from 'components/UI/StaffChip';
import Staff from 'model/Staff';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { buildMarketplaceStaffGroups } from './utils/staffElements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';

interface Props {
    service: Service;
    withoutStaff?: boolean;
    onEdit: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            flexDirection: 'column'
        },
        membersContainer: {
            display: 'flex',
            width: '100%',
            flexWrap: 'wrap'
        },
        titleContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            [theme.breakpoints.down('lg')]: {
                marginBottom: '10px'
            },
            [theme.breakpoints.up('xl')]: {
                marginBottom: '14px'
            },
            [theme.breakpoints.down('md')]: {
                marginBottom: '15px'
            }
        },
        title: {
            textTransform: 'uppercase',
            [theme.breakpoints.down('lg')]: {
                fontSize: '9px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '12px'
            },
            [theme.breakpoints.down('md')]: {
                textTransform: 'unset',
                fontSize: '14px',
                color: '#7A7A7A',
                fontWeight: '400'
            }
        },
        titleWithoutStaff: {
            [theme.breakpoints.down('md')]: {
                marginBottom: '-5px'
            }
        },
        marketplaceStaffSection: {
            'width': '100%',
            'marginTop': 16,

            '&:first-of-type': {
                marginTop: 0
            }
        },
        marketplaceHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            cursor: 'pointer',
            userSelect: 'none',
            paddingBottom: 8
        },
        marketplaceHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0
        },
        marketplaceStaffTitle: {
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.2,
            margin: 0,
            textTransform: 'uppercase',

            [theme.breakpoints.down('lg')]: {
                fontSize: '10px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '12px'
            },
            [theme.breakpoints.down('md')]: {
                textTransform: 'unset',
                fontSize: '14px',
                color: '#7A7A7A',
                fontWeight: 400
            }
        },
        marketplaceCount: {
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.2,
            margin: 0,
            whiteSpace: 'nowrap',

            [theme.breakpoints.down('lg')]: {
                fontSize: '10px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '12px'
            },
            [theme.breakpoints.down('md')]: {
                fontSize: '14px',
                color: '#7A7A7A',
                fontWeight: 400
            }
        },
        marketplaceChevron: {
            fontSize: 12,
            color: '#000',
            flexShrink: 0,

            [theme.breakpoints.down('md')]: {
                color: '#7A7A7A'
            }
        },
        marketplaceStaffContent: {
            marginTop: 10
        },
        memberContainer: {
            [theme.breakpoints.down('lg')]: {
                marginRight: '15px',
                marginBottom: '10px'
            },
            [theme.breakpoints.up('xl')]: {
                marginRight: '20px',
                marginBottom: '16px'
            },
            [theme.breakpoints.down('md')]: {
                'width': 'auto',
                'marginRight': '12px',
                'marginleft': '0px',
                'marginBottom': '12px',
                '&:nth-child(2n)': {
                    width: 'auto',
                    marginRight: '12px',
                    marginLeft: '0px'
                },
                '& > div': {
                    width: '100%',
                    maxWidth: '100%'
                },
                '& .MuiChip-label': {
                    fontWeight: '500',
                    fontSize: '14px'
                }
            }
        },
        noneStaffTitle: {
            fontWeight: 600,
            [theme.breakpoints.down('lg')]: {
                fontSize: '14px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '18px'
            }
        },
        noneStaffDescription: {
            [theme.breakpoints.down('lg')]: {
                fontSize: '10px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '14px'
            }
        }
    })
);

const renderStaffMembers = (staff: Array<StaffMember>, memberContainerClass: string) =>
    staff.map(member => (
        <Box
            key={member.id}
            className={memberContainerClass}
        >
            <StaffChip staff={member} />
        </Box>
    ));

export const ResumeServiceStaff: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const { organizationMarketplaces, organizationServicesInfo } = useOrganizationMarketplaces();
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);

    const existActiveStaff = useSelector<RootState, Array<Staff>>(state => state.staff.staff).length > 0;

    const marketplaceStaffGroups = useMemo(
        () =>
            buildMarketplaceStaffGroups(props.service.marketplaces, organizationServicesInfo, organizationMarketplaces),
        [organizationMarketplaces, organizationServicesInfo, props.service.marketplaces]
    );

    const hasGroupedStaff = marketplaceStaffGroups.length > 0;
    const hasStaffAssigned =
        marketplaceStaffGroups.some(group => group.staff.length > 0) || props.service.staff.length > 0;

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    const groupedStaff = (
        <Fragment>
            {marketplaceStaffGroups.map(group => {
                const isExpanded = expandedMarketplaceIds.includes(group.marketplaceId);
                const selectedCount = group.staff.length;
                const totalCount = group.totalAvailable;

                return (
                    <Box
                        key={group.marketplaceId}
                        className={classes.marketplaceStaffSection}
                    >
                        <Box
                            className={classes.marketplaceHeader}
                            onClick={() => toggleMarketplaceExpanded(group.marketplaceId)}
                        >
                            <Box className={classes.marketplaceHeaderLeft}>
                                <Typography className={classes.marketplaceStaffTitle}>
                                    {group.marketplaceName}
                                </Typography>
                                <Typography className={classes.marketplaceCount}>
                                    ({selectedCount}/{totalCount})
                                </Typography>
                            </Box>
                            <FontAwesomeIcon
                                icon={isExpanded ? faChevronUp : faChevronDown}
                                className={classes.marketplaceChevron}
                            />
                        </Box>
                        {isExpanded && (
                            <Box className={classes.marketplaceStaffContent}>
                                {group.staff.length > 0 ? (
                                    <Box className={classes.membersContainer}>
                                        {renderStaffMembers(group.staff, classes.memberContainer)}
                                    </Box>
                                ) : null}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Fragment>
    );

    const flatStaff = (
        <Box className={classes.membersContainer}>
            {renderStaffMembers(props.service.staff, classes.memberContainer)}
        </Box>
    );

    const staff = (
        <Fragment>
            <Box className={`${classes.titleContainer} ${!hasStaffAssigned ? classes.titleWithoutStaff : ''}`}>
                <Typography className={classes.title}>
                    {hasStaffAssigned ? 'Staff Members' : 'Staff not assigned'}
                </Typography>
            </Box>
            {hasGroupedStaff ? groupedStaff : hasStaffAssigned ? flatStaff : null}
        </Fragment>
    );

    const noneStaff = (
        <Fragment>
            <Box className={classes.titleContainer}>
                <Typography className={classes.noneStaffTitle}>Pending.</Typography>
            </Box>
            <Box>
                <Typography className={classes.noneStaffDescription}>No Staff have been created yet.</Typography>
            </Box>
        </Fragment>
    );

    return (
        <ResumeSection
            title="Staff"
            {...props}
            pending={!existActiveStaff}
        >
            <BodyResumeSection>
                <Box className={classes.container}>{!existActiveStaff ? noneStaff : staff}</Box>
            </BodyResumeSection>
        </ResumeSection>
    );
};

export default ResumeServiceStaff;
